/**
 * Leaguemate Scouting — S3 trade-style + reachability (pure, no I/O).
 *
 * From a manager's COMPLETE transactions across their dynasty/keeper TX set
 * (per-league input from scoutDeepFetch). A manager is identified per-league by
 * roster_id; the trade INITIATOR is the `creator` user_id (§7).
 *
 * Measurement carries (§7c): trade activity is normalized per league-season —
 * raw count is confounded by fan-out.
 */

import {
  TRADE_CONFIDENCE_MIN,
  INIT_REACTIVE_MAX,
  INIT_PROACTIVE_MIN,
  ACTIVITY_LOW_MAX,
  ACTIVITY_MODERATE_MAX,
  ACTIVITY_HIGH_MAX,
  REACH_ACTIVE_MAX_DAYS,
  REACH_COOLING_MAX_DAYS,
} from "./scoutConfig.js";

const DAY_MS = 86400000;
const valOf = (valueMap, pid) => valueMap?.get?.(String(pid))?.value ?? 0;

/**
 * Classify ONE trade from this manager's side (roster_id = rid).
 * Returns { structure, sentN, recvN, picksSent, picksRecv, faabInTrade,
 *           valueSent, valueRecv }.
 */
export function classifyTradeForManager(trade, rid, valueMap = new Map()) {
  const adds = trade.adds || {};
  const drops = trade.drops || {};
  const sent = Object.keys(drops).filter((pid) => drops[pid] === rid);
  const recv = Object.keys(adds).filter((pid) => adds[pid] === rid);
  const picks = Array.isArray(trade.draft_picks) ? trade.draft_picks : [];
  const picksSent = picks.filter((p) => p.previous_owner_id === rid && p.owner_id !== rid).length;
  const picksRecv = picks.filter((p) => p.owner_id === rid && p.previous_owner_id !== rid).length;
  const faabInTrade = Array.isArray(trade.waiver_budget) && trade.waiver_budget.length > 0;

  const sentN = sent.length;
  const recvN = recv.length;
  const picksInvolved = picksSent + picksRecv;

  let structure;
  if (picksInvolved > 0 && picksInvolved >= sentN + recvN) {
    structure = "pick-centric";
  } else if (sentN === 1 && recvN === 1) {
    structure = "1-for-1";
  } else if (sentN >= 2 && recvN <= 1) {
    structure = "consolidation"; // gave many to get one (acquiring concentration)
  } else if (recvN >= 2 && sentN <= 1) {
    structure = "expansion"; // gave one to get many (spreading)
  } else {
    structure = "balanced";
  }

  return {
    structure,
    sentN,
    recvN,
    picksSent,
    picksRecv,
    faabInTrade,
    valueSent: sent.reduce((s, pid) => s + valOf(valueMap, pid), 0),
    valueRecv: recv.reduce((s, pid) => s + valOf(valueMap, pid), 0),
  };
}

function bandActivity(perLeagueSeason) {
  if (perLeagueSeason <= ACTIVITY_LOW_MAX) return "low";
  if (perLeagueSeason <= ACTIVITY_MODERATE_MAX) return "moderate";
  if (perLeagueSeason <= ACTIVITY_HIGH_MAX) return "high";
  return "hyperactive";
}

const ACTIVITY_WORD = { low: "Light", moderate: "Occasional", high: "Active", hyperactive: "Hyperactive" };

/**
 * Trade-style fingerprint. `leagues` = scoutDeepFetch per-league rows; `uid` =
 * the manager's Sleeper user_id (for initiation). `nowMs` injectable for tests.
 */
export function computeTradeStyle(leagues, uid, valueMap = new Map(), { nowMs = Date.now() } = {}) {
  const trades = [];
  for (const lg of leagues || []) {
    const rid = lg.roster_id;
    for (const t of lg.completeTx || []) {
      if (t.type !== "trade") continue;
      if (!Array.isArray(t.roster_ids) || !t.roster_ids.includes(rid)) continue;
      trades.push({ ...classifyTradeForManager(t, rid, valueMap), creator: t.creator, created: t.created || 0 });
    }
  }

  const tradeCount = trades.length;
  const lowConfidence = tradeCount < TRADE_CONFIDENCE_MIN;
  const initiations = trades.filter((t) => t.creator === uid).length;
  const initiationRate = tradeCount ? Math.round((initiations / tradeCount) * 100) / 100 : null;
  const initiationBand =
    lowConfidence || initiationRate == null
      ? null
      : initiationRate < INIT_REACTIVE_MAX
      ? "reactive"
      : initiationRate > INIT_PROACTIVE_MIN
      ? "proactive"
      : "balanced";

  // Structure mix (fractions of this manager's trades).
  const counts = {};
  for (const t of trades) counts[t.structure] = (counts[t.structure] || 0) + 1;
  const structureMix = Object.fromEntries(
    Object.entries(counts).map(([k, n]) => [k, Math.round((n / Math.max(1, tradeCount)) * 100) / 100])
  );
  const dominantStructure =
    Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || null;

  const leagueSeasonCount = (leagues || []).length;
  const volumePerLeagueSeason = leagueSeasonCount ? Math.round((tradeCount / leagueSeasonCount) * 100) / 100 : 0;
  const activityBand = bandActivity(volumePerLeagueSeason);

  const faabInTradeRate = tradeCount
    ? Math.round((trades.filter((t) => t.faabInTrade).length / tradeCount) * 100) / 100
    : 0;

  const lastTradeMs = trades.reduce((m, t) => Math.max(m, t.created || 0), 0);
  const daysSinceLastTrade = lastTradeMs ? Math.round((nowMs - lastTradeMs) / DAY_MS) : null;

  // One-line verdict (gated by confidence).
  const parts = [`${ACTIVITY_WORD[activityBand]} dealer`];
  if (!lowConfidence && initiationRate != null) {
    const pct = Math.round(initiationRate * 100);
    parts.push(
      initiationBand === "proactive"
        ? `initiates most deals (~${pct}%)`
        : initiationBand === "reactive"
        ? `usually reacts (~${pct}%)`
        : `mixed initiation (~${pct}%)`
    );
  } else {
    parts.push(`thin trade sample (${tradeCount})`);
  }
  if (dominantStructure) parts.push(`leans ${dominantStructure}`);
  if (faabInTradeRate > 0) parts.push("uses FAAB as grease");
  const verdict = parts.join(", ");

  return {
    tradeCount,
    lowConfidence,
    initiationRate,
    initiationBand,
    structureMix,
    dominantStructure,
    leagueSeasonCount,
    volumePerLeagueSeason,
    activityBand,
    faabInTradeRate,
    daysSinceLastTrade,
    verdict,
  };
}

/**
 * Reachability (gating signal): days since last move (ANY move — trade, waiver,
 * or free-agent pickup) + total moves across the TX set.
 */
export function computeReachability(leagues, { nowMs = Date.now() } = {}) {
  let totalMoves = 0;
  let lastMoveMs = 0;
  const MOVE_TYPES = new Set(["trade", "waiver", "free_agent"]);
  for (const lg of leagues || []) {
    const rid = lg.roster_id;
    for (const t of lg.completeTx || []) {
      if (!MOVE_TYPES.has(t.type)) continue;
      if (!Array.isArray(t.roster_ids) || !t.roster_ids.includes(rid)) continue;
      totalMoves++;
      lastMoveMs = Math.max(lastMoveMs, t.created || 0);
    }
  }
  const daysSinceLastMove = lastMoveMs ? Math.round((nowMs - lastMoveMs) / DAY_MS) : null;
  const band =
    daysSinceLastMove == null
      ? "dormant"
      : daysSinceLastMove <= REACH_ACTIVE_MAX_DAYS
      ? "active"
      : daysSinceLastMove <= REACH_COOLING_MAX_DAYS
      ? "cooling"
      : "dormant";
  return { totalMoves, daysSinceLastMove, band };
}
